import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Flame, Loader2, Lock, Sparkles, Wand2, PenLine, BookOpen, Play, X,
  Archive, TrendingUp, Crown, PartyPopper, Target, Trash2, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { CardioGuide } from './CardioGuide';
import { CardioBuilder } from './CardioBuilder';
import { CardioAIGenerator } from './CardioAIGenerator';
import { GuidedCardioFlow } from './GuidedCardioFlow';
import { CardioSessionRunner } from './CardioSessionRunner';
import { CardioStats } from './CardioStats';
import { CardioLeaderboard } from './CardioLeaderboard';
import { ProgramCardMenu } from './ProgramCardMenu';
import { Confetti } from './RestTimer';
import { playCardioVoice } from './cardioAudio';
import { playVictoryFanfare } from './soundThemes';
import { estimateCardioSessionMinutes } from './cardioTimeEstimator';

const CreateChoiceModal = ({ open, onClose, onPickGuided, onPickManual, onPickAI, en }) => {
  const L = (fr, eng) => (en ? eng : fr);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="cardio-create-choice-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-3xl p-6 max-w-sm w-full space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black text-white">{L('Nouveau programme Cardio', 'New Cardio program')}</h3>
          <button onClick={onClose} data-testid="cardio-create-choice-close"><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <button onClick={onPickGuided} className="w-full text-left p-4 rounded-2xl bg-gradient-to-r from-emerald-500/15 to-orange-500/15 border border-emerald-500/30 hover:border-emerald-500/60 transition-colors flex items-center gap-3" data-testid="cardio-choice-guided">
          <Target className="w-6 h-6 text-emerald-400 shrink-0" />
          <div><p className="text-white font-bold text-sm">{L('Programme guidé', 'Guided program')}</p><p className="text-xs text-zinc-500">{L('Quelques questions, programme précis et immédiat (sans IA)', 'A few questions, precise instant program (no AI)')}</p></div>
        </button>
        <button onClick={onPickManual} className="w-full text-left p-4 rounded-2xl bg-white/[0.035] border border-white/10 hover:border-orange-500/40 transition-colors flex items-center gap-3" data-testid="cardio-choice-manual">
          <PenLine className="w-6 h-6 text-zinc-400 shrink-0" />
          <div><p className="text-white font-bold text-sm">{L('Créer manuellement', 'Create manually')}</p><p className="text-xs text-zinc-500">{L('Choisissez vous-même méthodes, exercices et temps', 'Choose your own methods, exercises and timings')}</p></div>
        </button>
        <button onClick={onPickAI} className="w-full text-left p-4 rounded-2xl bg-gradient-to-r from-orange-500/15 to-red-500/15 border border-orange-500/30 hover:border-orange-500/60 transition-colors flex items-center gap-3" data-testid="cardio-choice-ai">
          <Wand2 className="w-6 h-6 text-orange-400 shrink-0" />
          <div><p className="text-white font-bold text-sm">{L("Générer avec l'IA", 'Generate with AI')}</p><p className="text-xs text-zinc-500">{L('Questionnaire rapide, programme prêt en 1 minute', 'Quick questionnaire, program ready in 1 minute')}</p></div>
        </button>
      </div>
    </div>
  );
};

const RecordModal = ({ records, en, onClose }) => {
  const L = (fr, eng) => (en ? eng : fr);
  if (!records || records.length === 0) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="cardio-record-modal">
      <Confetti />
      <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gradient-to-br from-zinc-900 to-black border border-yellow-500/40 rounded-3xl p-8 max-w-sm w-full text-center">
        <PartyPopper className="w-14 h-14 text-yellow-400 mx-auto mb-4" />
        <h3 className="text-2xl font-black text-white mb-2">{L('Nouveau record personnel !', 'New personal record!')}</h3>
        {records.map((r, i) => (
          <p key={i} className="text-yellow-300 font-bold text-lg">{r.rounds_completed} {L('tours', 'rounds')} <span className="text-zinc-500 text-sm">({L('précédent', 'previous')}: {r.previous_best})</span></p>
        ))}
        <button onClick={onClose} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-yellow-500 to-orange-500" data-testid="cardio-record-close-btn">
          {L('Continuer', 'Continue')}
        </button>
      </motion.div>
    </div>
  );
};

/** Module Cardio dédié (HIIT/Tabata/EMOM/AMRAP/Intervalles) — onglet séparé de la musculation. */
export const CardioHub = ({ en, onBack, initialView = 'list' }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [statusTab, setStatusTab] = useState('active');
  const [programs, setPrograms] = useState([]);
  const [view, setView] = useState(initialView); // list | guide | guided | builder | ai | overview | runner | stats | leaderboard
  const [activeProgram, setActiveProgram] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [newRecords, setNewRecords] = useState(null);
  const [streakDays, setStreakDays] = useState(0);

  const loadPrograms = useCallback((status = statusTab) => {
    setLoading(true);
    const url = status === 'trash' ? '/api/tracking/cardio-pro/trash/list' : `/api/tracking/cardio-pro?status=${status}`;
    authFetch(url).then((r) => r.json()).then((d) => {
      setLocked(!!d.locked);
      setPrograms(d.programs || []);
    }).finally(() => setLoading(false));
  }, [authFetch, statusTab]);
  useEffect(() => { loadPrograms(statusTab); }, [statusTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    authFetch('/api/tracking/cardio-pro/stats/history').then((r) => r.json()).then((d) => setStreakDays(d.current_streak_days || 0)).catch(() => {});
  }, [authFetch]);

  const openProgram = (programId) => {
    authFetch(`/api/tracking/cardio-pro/${programId}`).then((r) => r.json()).then((data) => {
      setActiveProgram(data);
      setView('overview');
    });
  };

  const startSession = (session) => { setActiveSession(session); setView('runner'); };

  const submitLog = async (payload) => {
    try {
      const res = await authFetch(`/api/tracking/cardio-pro/${activeProgram.program_id}/log`, { method: 'POST', body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      toast.success(L('Séance cardio enregistrée !', 'Cardio session logged!'));
      const refreshed = await authFetch(`/api/tracking/cardio-pro/${activeProgram.program_id}`).then((r) => r.json());
      setActiveProgram(refreshed);
      setView('overview');
      if (data.new_records && data.new_records.length > 0) {
        setNewRecords(data.new_records);
        const voice = localStorage.getItem('cardio_voice_pref') || 'male';
        playVictoryFanfare();
        playCardioVoice(voice, 'record', en);
      }
    } catch {
      toast.error(L("Erreur lors de l'enregistrement.", 'Error saving the session.'));
    }
  };

  const duplicateProgram = (programId) => authFetch(`/api/tracking/cardio-pro/${programId}/duplicate`, { method: 'POST' }).then((r) => { if (!r.ok) throw new Error(); loadPrograms(); });
  const toggleArchiveProgram = (programId, archived) => authFetch(`/api/tracking/cardio-pro/${programId}/${archived ? 'unarchive' : 'archive'}`, { method: 'POST' }).then((r) => { if (!r.ok) throw new Error(); loadPrograms(); });
  const deleteProgram = (programId) => authFetch(`/api/tracking/cardio-pro/${programId}`, { method: 'DELETE' }).then((r) => { if (!r.ok) throw new Error(); loadPrograms(); });
  const restoreProgram = (programId) => authFetch(`/api/tracking/cardio-pro/${programId}/restore`, { method: 'POST' }).then((r) => {
    if (!r.ok) throw new Error('restore_failed');
    toast.success(L('Programme restauré !', 'Program restored!'));
    loadPrograms();
  }).catch(() => toast.error(L('Restauration impossible (délai de 24h dépassé ?).', 'Could not restore (past the 24h window?).')));
  const shareProgram = async (programId) => {
    const r = await authFetch(`/api/tracking/cardio-pro/${programId}/share`, { method: 'POST' });
    const d = await r.json();
    const url = `${window.location.origin}/programme-partage/${d.share_token}`;
    if (navigator.share) {
      await navigator.share({ title: 'Body-Up — Programme cardio', url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(L('Lien copié dans le presse-papiers !', 'Link copied to clipboard!'));
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>;

  if (locked) {
    return (
      <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-8 text-center" data-testid="cardio-locked">
        <Lock className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-white font-bold text-lg mb-2">{L('Débloquez le module Cardio', 'Unlock the Cardio module')}</h3>
        <p className="text-zinc-500 text-sm mb-5">{L('Passez à Suivi Pro+ ou à la Communauté pour créer vos séances cardio.', 'Upgrade to Suivi Pro+ or Communauté to build your cardio sessions.')}</p>
        <a href="/mon-espace?tab=profil&plan=solo" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-red-500" data-testid="cardio-locked-cta">{L('Débloquer mon accès', 'Unlock my access')}</a>
      </div>
    );
  }

  if (view === 'guide') return <CardioGuide en={en} onBack={() => setView('list')} />;
  if (view === 'stats') return <CardioStats en={en} onBack={() => setView('list')} />;
  if (view === 'leaderboard') return <CardioLeaderboard en={en} onBack={() => setView('list')} />;
  if (view === 'guided') return <GuidedCardioFlow en={en} onBack={() => setView('list')} onCreated={() => { setView('list'); loadPrograms('active'); }} />;
  if (view === 'builder') return <CardioBuilder en={en} onBack={() => setView('list')} onCreated={() => { setView('list'); loadPrograms('active'); }} />;
  if (view === 'ai') return <CardioAIGenerator en={en} onBack={() => setView('list')} onValidated={() => { setView('list'); loadPrograms('active'); }} />;
  if (view === 'runner' && activeSession) {
    return <CardioSessionRunner en={en} session={activeSession} authFetch={authFetch} onBack={() => setView('overview')} onSubmit={submitLog} />;
  }

  if (view === 'overview' && activeProgram) {
    return (
      <div className="space-y-4" data-testid="cardio-program-overview">
        <button onClick={() => setView('list')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="cardio-overview-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <h2 className="text-xl font-black text-white">{activeProgram.name}</h2>
        <p className="text-sm text-zinc-500">{activeProgram.goal}</p>
        <div className="space-y-3">
          {activeProgram.sessions.map((s, i) => (
            <div key={i} className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 flex items-center justify-between gap-3" data-testid={`cardio-overview-session-${i}`}>
              <div>
                <p className="text-white font-bold text-sm">{s.session_label}</p>
                <p className="text-xs text-zinc-500">{s.blocks.length} {L('bloc(s)', 'block(s)')} · ~{estimateCardioSessionMinutes(s)} min · {s.done_count || 0}/{s.sessions_target} {L('séances faites', 'sessions done')}</p>
              </div>
              <button onClick={() => startSession(s)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-105 transition-transform"
                data-testid={`cardio-start-session-btn-${i}`}>
                <Play className="w-4 h-4" /> {L('Démarrer', 'Start')}
              </button>
            </div>
          ))}
        </div>
        <RecordModal records={newRecords} en={en} onClose={() => setNewRecords(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="cardio-hub">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {onBack && <button onClick={onBack} className="text-zinc-400 hover:text-white" data-testid="cardio-hub-back-btn"><ChevronLeft className="w-5 h-5" /></button>}
          <h2 className="text-xl font-black text-white flex items-center gap-2"><Flame className="w-5 h-5 text-orange-400" /> {L('Cardio', 'Cardio')}</h2>
          {streakDays > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-black px-2.5 py-1 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-300 border border-orange-500/30" data-testid="cardio-streak-badge">
              <Flame className="w-3.5 h-3.5" /> {streakDays} {streakDays > 1 ? L('jours de suite', 'day streak') : L('jour de suite', 'day streak')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setView('guide')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 text-zinc-300 text-xs font-semibold hover:border-orange-500/40" data-testid="cardio-guide-btn">
            <BookOpen className="w-3.5 h-3.5" /> {L('Guide', 'Guide')}
          </button>
          <button onClick={() => setView('stats')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 text-zinc-300 text-xs font-semibold hover:border-orange-500/40" data-testid="cardio-stats-btn">
            <TrendingUp className="w-3.5 h-3.5" /> {L('Stats', 'Stats')}
          </button>
          <button onClick={() => setView('leaderboard')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 text-zinc-300 text-xs font-semibold hover:border-orange-500/40" data-testid="cardio-leaderboard-btn">
            <Crown className="w-3.5 h-3.5" /> {L('Classement', 'Ranking')}
          </button>
          <button onClick={() => setChoiceOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-105 transition-transform"
            data-testid="cardio-new-program-btn">
            <Sparkles className="w-4 h-4" /> {L('Nouveau', 'New')}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {[{ v: 'active', label: L('Actifs', 'Active') }, { v: 'archived', label: L('Archivés', 'Archived') }, { v: 'trash', label: L('Corbeille', 'Trash') }].map((t) => (
          <button key={t.v} onClick={() => setStatusTab(t.v)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${statusTab === t.v ? 'bg-white/15 text-white' : 'bg-transparent text-zinc-500 hover:text-zinc-300'}`}
            data-testid={`cardio-status-tab-${t.v}`}>
            {t.v === 'archived' && <Archive className="w-3 h-3 inline mr-1" />}{t.v === 'trash' && <Trash2 className="w-3 h-3 inline mr-1" />}{t.label}
          </button>
        ))}
      </div>

      {programs.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-8 text-center text-zinc-500 text-sm" data-testid="cardio-no-programs">
          {statusTab === 'archived'
            ? L('Aucun programme archivé.', 'No archived program.')
            : statusTab === 'trash'
            ? L('Corbeille vide.', 'Trash is empty.')
            : L('Aucun programme cardio pour le moment. Créez-en un pour commencer !', 'No cardio program yet. Create one to get started!')}
        </div>
      ) : statusTab === 'trash' ? (
        <div className="space-y-3">
          {programs.map((p) => {
            const hoursLeft = Math.max(0, Math.ceil(24 - (Date.now() - new Date(p.deleted_at).getTime()) / 3600000));
            return (
              <div key={p.program_id} className="w-full rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-5" data-testid={`cardio-trash-program-card-${p.program_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-bold text-lg truncate">{p.name}</p>
                    <p className="text-xs text-zinc-500">{L(`Supprimé — récupérable encore ${hoursLeft}h`, `Deleted — recoverable for ${hoursLeft}h more`)}</p>
                  </div>
                  <button onClick={() => restoreProgram(p.program_id)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-orange-500/20 border border-orange-500/40 hover:bg-orange-500/30 transition-colors" data-testid={`cardio-trash-restore-btn-${p.program_id}`}>
                    <RotateCcw className="w-3.5 h-3.5" /> {L('Restaurer', 'Restore')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {programs.map((p) => {
            const pct = Math.min(100, Math.round((p.total_done / Math.max(1, p.total_target)) * 100));
            return (
              <div key={p.program_id} onClick={() => openProgram(p.program_id)} role="button"
                className="w-full text-left rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 hover:border-orange-500/40 p-5 transition-colors cursor-pointer"
                data-testid={`cardio-program-card-${p.program_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-bold text-lg truncate">{p.name}</p>
                    <p className="text-sm text-zinc-500 truncate">{p.goal}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-black px-2.5 py-1 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-300 border border-orange-500/30">{pct}%</span>
                    <ProgramCardMenu en={en} isArchived={statusTab === 'archived'} testIdPrefix={`cardio-program-${p.program_id}`}
                      onDuplicate={() => duplicateProgram(p.program_id)}
                      onToggleArchive={() => toggleArchiveProgram(p.program_id, statusTab === 'archived')}
                      onShare={() => shareProgram(p.program_id)}
                      onDelete={() => deleteProgram(p.program_id)} />
                  </div>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-gradient-to-r from-orange-500 to-red-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateChoiceModal open={choiceOpen} en={en} onClose={() => setChoiceOpen(false)}
        onPickGuided={() => { setChoiceOpen(false); setView('guided'); }}
        onPickManual={() => { setChoiceOpen(false); setView('builder'); }}
        onPickAI={() => { setChoiceOpen(false); setView('ai'); }} />
    </div>
  );
};

export default CardioHub;
